/**
 * lib/examRoomCatalogue.ts — which rooms the office may seat an exam in.
 * ------------------------------------------------------------------
 * Exam rooms used to be typed in by hand, one at a time, before a session
 * could be seated at all. The school's rooms are already recorded — the
 * timetable writes `timetableRooms/list` — so the picker offers those, and
 * the office only says how many desks fit.
 *
 * THE FOUR EXAM ROOMS COME FIRST (the school, 2026-09-22): the Hall, the AV
 * Room, the Multipurpose Room and E18 are where exams are actually sat, so
 * they are at the top of the list whether or not the timetable knows them —
 * the Hall and the AV Room are not teaching rooms and so appear in no slot.
 * Everything else follows, grouped Block A to Block H, with the labs and the
 * rooms whose names carry no block after them.
 *
 * A NAME IS NOT AN ID. A room the office picks becomes a `reportExamRooms`
 * document with its own id, so renaming a teaching room later cannot orphan a
 * seating plan that is already printed.
 */

export interface RoomChoice {
  /** As it will be shown and stored. */
  name: string;
  /** Group heading: 'Exam rooms', 'Block A'… 'Labs and other rooms'. */
  group: string;
  /** Already a `reportExamRooms` document — the picker shows it as added. */
  existingId?: string;
}

/** The rooms exams are sat in, in the order the office thinks of them. */
export const DEFAULT_EXAM_ROOMS = ['Hall', 'AV Room', 'Multipurpose Room', 'E18'] as const;

export const EXAM_GROUP = 'Exam rooms';
export const OTHER_GROUP = 'Labs and other rooms';

/**
 * The timetable writes MP_Rm; the office says Multipurpose Room. Without this
 * the same room is offered twice, and a session could be seated in both.
 */
const ALIASES: Record<string, string> = {
  mp_rm: 'Multipurpose Room',
  'mp rm': 'Multipurpose Room',
  mp: 'Multipurpose Room',
  'multi purpose room': 'Multipurpose Room',
  'av rm': 'AV Room',
  'a/v room': 'AV Room',
  'school hall': 'Hall',
  'main hall': 'Hall',
};

export function canonicalRoomName(raw: string): string {
  const name = raw.trim().replace(/\s+/g, ' ');
  return ALIASES[name.toLowerCase()] ?? name;
}

/** "E18" → "Block E"; a lab or an odd name has no block. */
export function blockOf(name: string): string | null {
  const m = canonicalRoomName(name).match(/^([A-H])\s*-?\s*\d+/i);
  return m ? `Block ${m[1].toUpperCase()}` : null;
}

const groupRank = (group: string): number => {
  if (group === EXAM_GROUP) return 0;
  if (group.startsWith('Block ')) return 1 + (group.charCodeAt(6) - 65);
  return 99;
};

/** Natural order inside a block: E2 before E11. */
const byName = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export interface CatalogueInput {
  /** `timetableRooms/list`, as written by the authoring app. */
  timetableRooms?: string[];
  /** Exam rooms already created, so the picker can show them as added. */
  existing?: { id: string; name: string }[];
}

/**
 * Every room the office may choose, grouped and ordered.
 *
 * A timetable "room" of several rooms at once ("BB | C4 | C5") is a combined
 * teaching slot, not a place with desks in it, and is left out.
 */
export function roomCatalogue(input: CatalogueInput): { group: string; rooms: RoomChoice[] }[] {
  const existingByName = new Map(
    (input.existing ?? []).map((r) => [canonicalRoomName(r.name).toLowerCase(), r.id]),
  );

  const names = new Map<string, string>(); // lower -> display
  const add = (raw: string) => {
    const name = canonicalRoomName(raw);
    if (!name || name.includes('|')) return;
    names.set(name.toLowerCase(), name);
  };
  DEFAULT_EXAM_ROOMS.forEach(add);
  (input.timetableRooms ?? []).forEach(add);
  (input.existing ?? []).forEach((r) => add(r.name));

  const defaults = new Set(DEFAULT_EXAM_ROOMS.map((n) => n.toLowerCase()));
  const groups = new Map<string, RoomChoice[]>();
  for (const [lower, name] of names) {
    const group = defaults.has(lower) ? EXAM_GROUP : blockOf(name) ?? OTHER_GROUP;
    const list = groups.get(group) ?? [];
    list.push({ name, group, existingId: existingByName.get(lower) });
    groups.set(group, list);
  }

  return [...groups.entries()]
    .map(([group, rooms]) => ({
      group,
      rooms: group === EXAM_GROUP
        // The school's own order, not the alphabet: Hall first.
        ? rooms.sort((a, b) => DEFAULT_EXAM_ROOMS.indexOf(a.name as typeof DEFAULT_EXAM_ROOMS[number])
          - DEFAULT_EXAM_ROOMS.indexOf(b.name as typeof DEFAULT_EXAM_ROOMS[number]))
        : rooms.sort((a, b) => byName(a.name, b.name)),
    }))
    .sort((a, b) => groupRank(a.group) - groupRank(b.group) || byName(a.group, b.group));
}

/**
 * A starting grid for a room nobody has measured yet — the office corrects it
 * on the spot. A hall is wide and deep; a classroom is a classroom.
 */
export function suggestedDesks(name: string): { cols: number; rows: number } {
  const n = canonicalRoomName(name).toLowerCase();
  if (n === 'hall') return { cols: 8, rows: 10 };
  if (n === 'multipurpose room') return { cols: 6, rows: 8 };
  if (n === 'av room') return { cols: 5, rows: 5 };
  if (/lab/.test(n)) return { cols: 4, rows: 5 };
  return { cols: 4, rows: 6 };
}
